package com.example.mystybag.common.extensions

import androidx.compose.foundation.Image
import androidx.compose.foundation.gestures.FlingBehavior
import androidx.compose.foundation.gestures.ScrollableDefaults
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyListScope
import androidx.compose.foundation.lazy.LazyListState
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.ripple.RippleAlpha
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.RippleConfiguration
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.NonRestartableComposable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.graphics.DefaultAlpha
import androidx.compose.ui.graphics.FilterQuality
import androidx.compose.ui.graphics.painter.Painter
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.unit.Dp
import coil3.compose.AsyncImage
import coil3.compose.AsyncImagePainter
import coil3.compose.LocalPlatformContext
import coil3.request.ImageRequest
import coil3.request.crossfade
import coil3.size.Precision
import coil3.size.Size
import dev.icerock.moko.resources.ImageResource
import dev.icerock.moko.resources.compose.painterResource
import com.example.mystybag.core.resources.SRC
import com.example.mystybag.core.ui.theme.LocalTheme.p0
import com.example.mystybag.core.views.AppTheme
import com.example.mystybag.core.views.keyboardListenerFlow
import com.example.mystybag.common.expected.resolvePlatformLinkForAsyncImage
import com.example.mystybag.common.expected.SCImageView
import com.example.mystybag.common.model.ListVisualModel
import com.example.mystybag.communication.defines.CommunicationDefines.Companion.DOMAIN
import com.example.mystybag.common.ui.components.custom.ChooseItemRowView
import com.example.mystybag.sharedmodels.SCImage
import kotlinx.coroutines.flow.collectLatest

@Composable
fun Image(
    painter      : Painter,
    modifier     : Modifier = Modifier,
    alignment    : Alignment = Alignment.Center,
    contentScale : ContentScale = ContentScale.Fit,
    alpha        : Float = DefaultAlpha,
    colorFilter  : ColorFilter? = null
) = Image(
    painter             = painter,
    contentDescription  = null,
    modifier            = modifier,
    alignment           = alignment,
    contentScale        = contentScale,
    alpha               = alpha,
    colorFilter         = colorFilter
)

@Composable
fun LazyShimmerColumn(
    modifier            : Modifier                                      = Modifier,
    isLoading           : Boolean,
    shimmerItem         : (LazyListScope.(isLoading: Boolean) -> Unit)? = null,
    state               : LazyListState                                 = rememberLazyListState(),
    contentPadding      : PaddingValues                                 = PaddingValues(p0),
    reverseLayout       : Boolean                                       = false,
    verticalArrangement : Arrangement.Vertical                          = if (!reverseLayout) Arrangement.Top else Arrangement.Bottom,
    horizontalAlignment : Alignment.Horizontal                          = Alignment.Start,
    flingBehavior       : FlingBehavior                                 = ScrollableDefaults.flingBehavior(),
    userScrollEnabled   : Boolean                                       = true,
    content             : LazyListScope.() -> Unit
) = LazyColumn(
    modifier            = modifier.scrollEdgeFade(),
    state               = state,
    contentPadding      = contentPadding,
    reverseLayout       = reverseLayout,
    verticalArrangement = verticalArrangement,
    horizontalAlignment = horizontalAlignment,
    flingBehavior       = flingBehavior,
    userScrollEnabled   = userScrollEnabled
) {
    if (isLoading)
        shimmerItem?.invoke(this, isLoading)
    else
        content.invoke(this)
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AppThemeRippleConfig(): RippleConfiguration {
    return RippleConfiguration(color = AppTheme.current.colors.primary, rippleAlpha = RippleAlpha(0.1f, 0.1f, 0.1f, 0.1f))
}

@Composable
@NonRestartableComposable
fun AsyncImage(
    modifier        : Modifier                                      = Modifier,
    link            : String?                                       = null,
    image           : SCImage?                                      = null,
    placeholder     : ImageResource?                                = SRC.images.img_placeholder,
    error           : ImageResource?                                = null,
    alignment       : Alignment                                     = Alignment.Center,
    contentScale    : ContentScale                                  = ContentScale.Fit,
    onLoading       : ((AsyncImagePainter.State.Loading) -> Unit)?  = null,
    onSuccess       : ((AsyncImagePainter.State.Success) -> Unit)?  = null,
    onError         : ((AsyncImagePainter.State.Error) -> Unit)?    = null,
    onSuccessSimple : (() -> Unit)?                                  = null,
    onErrorSimple   : (() -> Unit)?                                  = null,
    alpha           : Float                                         = DefaultAlpha,
    colorFilter     : ColorFilter?                                  = null,
    filterQuality   : FilterQuality                                 = FilterQuality.Low,
    useOriginalSize : Boolean                                       = false,
    cornerRadius    : Dp?                                           = null
) {
    val resolvedModifier = if (cornerRadius != null) {
        modifier.clip(RoundedCornerShape(cornerRadius))
    } else {
        modifier
    }

    if (!SCImageView(
            image           = image,
            modifier        = resolvedModifier,
            alignment       = alignment,
            contentScale    = contentScale,
            colorFilter     = colorFilter
        )
    ) {
        // Guard: Coil throws NullRequestDataException when request.data is null.
        // Also, iOS DNS for `mystybag.com` may be missing an A record; normalize to `www.mystybag.com`.
        val normalizedLink = remember(link) { link.normalizeForCoil() }

        // If we have neither an SCImage nor a valid link, just show a placeholder (or nothing).
        if (image == null && normalizedLink == null) {
            placeholder?.let {
                Image(
                    painter      = painterResource(it),
                    modifier     = resolvedModifier,
                    alignment    = alignment,
                    contentScale = contentScale,
                    alpha        = alpha,
                    colorFilter  = colorFilter
                )
            }
            return
        }

        val asyncData = if (image != null) image else normalizedLink?.let(::resolvePlatformLinkForAsyncImage)

        AsyncImage(
            model               = ImageRequest
                .Builder(LocalPlatformContext.current)
                .data(asyncData)
                .precision(Precision.EXACT)
                .crossfade(false)
                .apply {
                    if (useOriginalSize)
                        size(Size.ORIGINAL)
                }
                .build(),
            contentDescription  = "",
            modifier            = resolvedModifier,
            error               = error?.let { painterResource(it) },
            placeholder         = placeholder?.let { painterResource(placeholder) },
            alignment           = alignment,
            contentScale        = contentScale,
            onLoading           = onLoading,
            onSuccess           = { state ->
                onSuccess?.invoke(state)
                onSuccessSimple?.invoke()
            },
            onError             = { state ->
                onError?.invoke(state)
                onErrorSimple?.invoke()
            },
            alpha               = alpha,
            colorFilter         = colorFilter,
            filterQuality       = filterQuality
        )
    }
}

private fun String?.normalizeForCoil(): String? {
    val value = this?.trim().orEmpty()
    if (value.isEmpty()) return null

    // `mystybag.com` does not have an A record (DNS fails), while `www.mystybag.com` /
    // `staging.mystybag.com` do. Route bare `mystybag.com` to the currently
    // active DOMAIN (production builds → www.mystybag.com, development /
    // staging builds → staging.mystybag.com). Keep other hosts unchanged.
    val activeDomain = DOMAIN
    return when {
        value.startsWith("https://mystybag.com/") -> value.replaceFirst("https://mystybag.com/", "https://$activeDomain/")
        value.startsWith("http://mystybag.com/") -> value.replaceFirst("http://mystybag.com/", "https://$activeDomain/")
        value.startsWith("mystybag.com/") -> "https://$activeDomain/" + value.removePrefix("mystybag.com/")
        else -> value
    }
}

@Composable
fun <T: ListVisualModel> ShimmerListColumn(
    shimmerItem : LazyListScope.(isLoading: Boolean) -> Unit,
    modifier    : Modifier      = Modifier,
    list        : List<T>       = listOf(),
    isLoading   : Boolean       = false,
    key         : (T) -> Any,
    onClick     : (T) -> Unit   = {}
) {
    LazyShimmerColumn(
        modifier            = modifier.roundedContainer(),
        isLoading           = isLoading,
        shimmerItem         = shimmerItem
    ) {
        items(items = list, key = key) {item ->
            ChooseItemRowView(
                link        = item.itemImageLink,
                text        = item.itemText,
                onClick     = remember(item.itemText) { { onClick(item) } }
            )
        }
    }
}

@Composable
fun clearFocusOnKeyboardClosed() {
    val focusManager    = LocalFocusManager.current

    LaunchedEffect(Unit) {
        keyboardListenerFlow.collectLatest {
            if (!it)
                focusManager.clearFocus()
        }
    }
}
val WindowInsets.Companion.Zero : WindowInsets
    get() = WindowInsets(0, 0, 0, 0)